import { clearRegistrationUser } from 'utils/fns';

/**
 * Forma mínima reconhecida pelo dedup. Cobre as duas fontes de participante:
 * `bb_task__c.team` (`_id`/`chave` + `manager`) e `find/realizations.team_data`
 * (`_id`/`chave` + `is_manager`).
 */
export interface ParticipanteDeduplicavel {
  _id?: string;
  chave?: string;
  matricula?: string;
  manager?: boolean;
  is_manager?: boolean;
}

/** Matrícula normalizada (sem "F" e sem zeros à esquerda) ou '' quando ausente. */
const identificar = (participante: ParticipanteDeduplicavel): string => {
  const bruta = participante?.chave ?? participante?.matricula ?? participante?._id;
  return bruta ? clearRegistrationUser(String(bruta)) : '';
};

const isGestor = (participante: ParticipanteDeduplicavel): boolean =>
  participante?.manager === true || participante?.is_manager === true;

/**
 * Remove participantes repetidos dentro de uma mesma iniciativa, mantendo a
 * ordem original. Quando a mesma matrícula aparece mais de uma vez — caso do
 * gestor que também é participante — prevalece a entrada com perfil de gestor.
 *
 * Entradas sem matrícula identificável são preservadas: não há como deduplicá-las
 * com segurança.
 *
 * O escopo é sempre uma única iniciativa. Uma pessoa em duas iniciativas
 * distintas continua aparecendo em ambas.
 */
export function dedupParticipantes<T extends ParticipanteDeduplicavel>(
  participantes: T[] | null | undefined
): T[] {
  const unicos: T[] = [];
  const posicaoPorMatricula = new Map<string, number>();

  (participantes ?? []).forEach((participante) => {
    const matricula = identificar(participante);

    if (!matricula) {
      unicos.push(participante);
      return;
    }

    const posicao = posicaoPorMatricula.get(matricula);
    if (posicao === undefined) {
      posicaoPorMatricula.set(matricula, unicos.length);
      unicos.push(participante);
      return;
    }

    if (isGestor(participante) && !isGestor(unicos[posicao])) {
      unicos[posicao] = participante;
    }
  });

  return unicos;
}
